import { useUser } from '@app/context/UserContext';
import { useQueryParams } from '@app/hooks/useQuestionaireRoute';
import { DocumentShareToken, parseJwt } from '@app/utils/parseJwt';
import { useCompanyBasicQuery, useCompanyQuery } from '@dieterApi/company/useCompanyQuery';
import { DpoTaskResourceType, useDpoTasksQuery } from '@dieterApi/dpo/useDpoTasksQuery';
import { useGetShareTokenJWTQuery } from '@dieterApi/sharetoken/useGetShareTokenJWTQuery';
import { useTopicsQuery } from '@dieterApi/topic/useTopicsQuery';
import {
  ApplicationType,
  ProductFeature,
  User,
  UserQuestionnaireApplicationTopic,
  UserRole,
} from '@dieterApi/user/useUserQuery';
import { useWhitelabelConfigQuery } from '@dieterApi/whitelabel/useWhitelabelConfigQuery';
import { useMemo } from 'react';

export interface UserWithMethods extends User {
  hasFeature: (feature: ProductFeature) => boolean;
  isReadOnly: boolean;
  isDpo: boolean;
}

/**
 * Hook that combines user data with full company data.
 * Use this hook when you need access to detailed company information
 * that is not included in the minimal user.company object.
 *
 * @returns user object with minimal company data, full company object, whitelabel config, and loading states
 */
export function useUserWithCompany() {
  const { user, userLoading } = useUser();
  // Fetch full company data for the active company
  const { data: companyData, loading: companyLoading } = useCompanyQuery(user?.company?.id || '');
  const { data: topicsData, loading: topicsLoading, refetch: refetchTopics } = useTopicsQuery(user?.company?.id || '');

  const company = companyData?.getCompany;
  const isDpoMode = company?.features.includes('DpoManaged') ?? false;
  const companyId = company?.id;

  // Fetch DPO task states if in DPO mode
  const {
    data: dpoTasksData,
    loading: dpoTasksLoading,
    refetch: refetchDpoTasks,
  } = useDpoTasksQuery(isDpoMode ? companyId : undefined);
  const dpoTasks = dpoTasksData?.getDpoTasks || [];

  // Create lookup sets for enabled items (O(1) lookup performance)
  const enabledApplicationIds = useMemo(() => {
    if (!isDpoMode) return null; // null means "all enabled"
    const set = new Set<string>();
    dpoTasks.forEach((task) => {
      if (task.type === DpoTaskResourceType.Questionnaire && task.isEnabled) {
        // Extract applicationId from task.id format: company-{companyId}-application-{appId}
        const match = task.id.match(/company-.+-application-(.+)$/);
        if (match) {
          set.add(match[1]);
        }
      }
    });
    return set;
  }, [dpoTasks, isDpoMode]);

  // Helper function to check if items are enabled
  const isApplicationEnabled = (applicationId: string): boolean => {
    if (dpoTasksLoading) return false;
    if (!isDpoMode) return true;
    if (!enabledApplicationIds) return true;
    return enabledApplicationIds.has(applicationId);
  };

  // Filter topics and applications based on DPO settings
  const allTopics = topicsData?.getTopics || [];
  const topics = useMemo(() => {
    if (!isDpoMode || dpoTasksLoading) return allTopics;

    return allTopics
      .map((topic) => ({
        ...topic,
        applications: topic.applications.filter(
          (app) =>
            [ApplicationType.ListContract, ApplicationType.ListEntryLong, ApplicationType.ListEntryShort].includes(
              app.type
            ) || isApplicationEnabled(app.id)
        ),
      }))
      .filter((topic) => {
        // Keep topic if it has at least one Primary or Secondary application,
        // or if it has an enabled application in DPO tasks (e.g. topic-dsfa with ListContract)
        const nonListContractApps = topic.applications.filter((app) =>
          [ApplicationType.Primary, ApplicationType.Secondary].includes(app.type)
        );
        const hasEnabledListApp = topic.applications.some(
          (app) => app.type === ApplicationType.ListContract && isApplicationEnabled(app.id)
        );
        return nonListContractApps.length > 0 || hasEnabledListApp;
      });
  }, [allTopics, isDpoMode, dpoTasksLoading, enabledApplicationIds]);

  const applications = topics.flatMap((topic) => topic.applications);

  // Flatten topics hierarchy into questionnaires array
  // Each questionnaire contains reference to its application and topic
  const questionnaires: UserQuestionnaireApplicationTopic[] = topics.flatMap((topic) =>
    topic.applications.flatMap((application) =>
      (application.questionnaires || []).map((questionnaire) => ({
        ...questionnaire,
        application: {
          ...application,
          topic: topic,
        },
      }))
    )
  );

  const { data: principalCompanyData, loading: principalCompanyLoading } = useCompanyBasicQuery(
    user?.companies.find((c) => c.isPrincipal)?.id || ''
  );
  const { data: whitelabelConfigData, loading: whitelabelLoading } = useWhitelabelConfigQuery();

  const query = useQueryParams();
  const token = query.get('token') || '';

  const { data: shareTokenJWT, loading: tokenLoading } = useGetShareTokenJWTQuery({
    variables: { id: token },
  });

  const jwt = shareTokenJWT?.getShareTokenJWT?.jwt;
  const tokenInfo = jwt ? parseJwt<DocumentShareToken>(jwt)! : undefined;
  const whitelabelConfigFromToken = tokenInfo?.whitelabelConfigJson ? JSON.parse(tokenInfo.whitelabelConfigJson) : null;

  const principalCompany = principalCompanyData?.getCompany;

  // we have three possible sources for whitelabel config
  // 1. from the token (highest priority)
  // 2. from the user's active company (if any)
  // 3. from the global query (host based matching) (lowest priority)
  const whitelabelConfig =
    whitelabelConfigFromToken || company?.whitelabelConfig || whitelabelConfigData?.whitelabelConfig;

  let userWithMethods: UserWithMethods | undefined = undefined;

  // add custom methods and attributes to user object
  if (user) {
    // copy user object but allow extension
    userWithMethods = { ...user } as UserWithMethods;
    userWithMethods.hasFeature = function (feature: ProductFeature) {
      return company?.features.includes(feature) || false;
    };
    userWithMethods.isReadOnly = user.role === UserRole.ReadOnly;
    userWithMethods.isDpo = user.role === UserRole.Dpo;
  }

  const questionnairesWithoutDpoFilter: UserQuestionnaireApplicationTopic[] = allTopics.flatMap((topic) =>
    topic.applications.flatMap((application) =>
      (application.questionnaires || []).map((questionnaire) => ({
        ...questionnaire,
        application: {
          ...application,
          topic: topic,
        },
      }))
    )
  );

  return {
    user: userWithMethods,
    userLoading,
    activeCompany: company,
    activeCompanyLoading: companyLoading,
    topics,
    refetchTopics,
    applications,
    topicsLoading,
    questionnaires,
    principalCompany,
    principalCompanyLoading,
    whitelabelConfig,
    whitelabelLoading: tokenLoading || whitelabelLoading,
    loading: userLoading || companyLoading || tokenLoading || whitelabelLoading || topicsLoading,
    // Non-DPO-filtered data for Status page and list items
    topicsWithoutDpoFilter: allTopics,
    questionnairesWithoutDpoFilter,
    // DPO-specific state
    dpoTasks,
    dpoTasksLoading,
    isDpoMode,
    refetchDpoTasks,
  };
}
